import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    Card,
    CardContent,
    Container,
    Box,
    Typography,
    Button,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { APP_IMAGES } from "../../constants/AppImages";
import { APP_COLORS } from "../../theme/appTheme";
import { intl, ROUTES } from "dmsbasereact";
import { useCreateAccountForm } from "./useCreateAccountForm";
import { CreateAccountForm } from "./components/CreateAccountForm";
import { SuccessModal } from "./components/SuccessModal";
import { LegalDocumentDialog } from "./components/LegalDocumentDialog";
import { getBrazilianStateOptions } from "../../constants/BrazilianStates";
import { UserTypeEnum } from "../../constants/UserType";

export const CreateAccountPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { state, handlers } = useCreateAccountForm(searchParams);

    const {
        status,
        message,
        documentDialogOpen,
        legalDocument,
        loadingDocument,
        successModalOpen,
        createdUserName,
        countdown,
    } = state;

    const {
        handleCloseDocument,
        handleSuccessModalClose,
    } = handlers;
    
    const stateOptions = getBrazilianStateOptions();

    const userTypeOptions = Object.values(UserTypeEnum).map(
        ({ label, value }) => ({
            label,
            value,
        })
    );

    return (
        <Container
            maxWidth="false"
            disableGutters
            sx={{
                background: '#FFFFFF',
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100vh",
                    width: "100%",
                    maxWidth: "800px",
                    px: 2,
                }}
            >
                <Box
                    sx={{
                        mb: 4,
                        textAlign: "center",
                    }}
                >
                    <Box
                        component="img"
                        src={APP_IMAGES.LOGO}
                        alt="Lena Logo"
                        sx={{
                            width: "150px",
                            height: "auto",
                            mb: 2,
                            filter: "drop-shadow(2px 2px 8px rgba(0,0,0,0.3))",
                        }}
                    />
                </Box>
                <Card
                    sx={{
                        width: "100%",
                        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
                        borderRadius: 3,
                    }}
                >
                    <CardContent sx={{ p: 4 }}>
                        <>
                            {status === 'error' && (
                                <Box sx={{ textAlign: 'center', py: 2 }}>
                                    <ErrorOutlineIcon
                                        sx={{
                                            fontSize: 80,
                                            color: 'error.main',
                                            mb: 2,
                                        }}
                                    />
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'error.main' }}>
                                        {intl.get("CreateAccountError").d("Erro ao criar conta")}
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                                        {message}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => navigate(ROUTES.Login)}
                                        size="large"
                                        sx={{ minWidth: 200 }}
                                    >
                                        {intl.get("GoToLogin").d("Go to Login")}
                                    </Button>
                                </Box>
                            )}
                            {status === 'success' && (
                                <>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            textAlign: "center",
                                            mb: 1,
                                            fontWeight: 600,
                                            color: APP_COLORS.neutral.gray900,
                                        }}
                                    >
                                        {intl.get("CreateAccount").d("Create Account")}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: "center",
                                            mb: 3,
                                            color: APP_COLORS.neutral.gray600,
                                        }}
                                    >
                                        {intl.get("RegisterSubtitle").d("Sign up to get started")}
                                    </Typography>
                                    <CreateAccountForm 
                                        state={state}
                                        handlers={handlers}
                                        stateOptions={stateOptions}
                                        userTypeOptions={userTypeOptions}
                                    />
                                </>
                            )}
                        </>
                    </CardContent>
                </Card>
                <SuccessModal 
                    open={successModalOpen}
                    onClose={handleSuccessModalClose}
                    createdUserName={createdUserName}
                    countdown={countdown}
                />
                <LegalDocumentDialog
                    open={documentDialogOpen}
                    onClose={handleCloseDocument}
                    document={legalDocument}
                    loading={loadingDocument}
                />
            </Box>
        </Container>
    )
};
